import { inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { tapResponse } from '@ngrx/operators';
import { signalStore, withMethods } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { MessageService } from 'primeng/api';
import { type Observable, forkJoin, pipe, switchMap, take } from 'rxjs';

/** Supported notification severity types aligned with PrimeNG Toast. */
type NotificationSeverity = 'success' | 'info' | 'warn' | 'error';

type NotificationConfig = {
  type: NotificationSeverity;
  scope: string;
  titleKey: string;
  messageKey: string;
  duration?: number;
  params?: Record<string, unknown>;
  titleParams?: Record<string, unknown>;
};

/**
 * Signal store that centralizes user notifications using PrimeNG MessageService with i18n.
 *
 * Provides a method to translate title and detail keys via {@link TranslocoService} and
 * display them with {@link MessageService} (PrimeNG Toast `summary` + `detail`).
 *
 * @example
 * const store = inject(NotificationStore);
 * store.notify({
 *   config: {
 *     type: 'success',
 *     scope: 'common',
 *     titleKey: 'savedTitle',
 *     messageKey: 'saved',
 *   },
 * });
 */
export const NotificationStore = signalStore(
  { providedIn: 'root' },
  withMethods(
    (
      store,
      messageService = inject(MessageService),
      translocoService = inject(TranslocoService)
    ) => ({
      /**
       * Translates title and detail keys and displays the notification via PrimeNG Toast.
       *
       * @param config - Notification configuration including type, i18n scope, title/detail keys, duration and params.
       * @returns void
       * @example
       * notificationStore.notify({
       *   config: {
       *     type: 'info',
       *     scope: 'common',
       *     titleKey: 'loadingTitle',
       *     messageKey: 'loading',
       *   },
       * });
       */
      notify: rxMethod<{ config: NotificationConfig }>(
        pipe(
          switchMap(({ config }) => {
            const scopeOptions = { scope: config.scope };

            return forkJoin({
              detail: translocoService
                .selectTranslate(config.messageKey, config.params, scopeOptions)
                .pipe(take(1)),
              title: translocoService
                .selectTranslate(
                  config.titleKey,
                  config.titleParams ?? config.params,
                  scopeOptions
                )
                .pipe(take(1)),
            } satisfies Record<string, Observable<string>>).pipe(
              tapResponse({
                next: ({ detail, title }) => {
                  messageService.add({
                    detail,
                    life: config.duration ?? 4000,
                    severity: config.type,
                    summary: title,
                  });
                },
                error: () => {
                  messageService.add({
                    detail: config.messageKey,
                    life: config.duration ?? 4000,
                    severity: config.type,
                    summary: config.titleKey,
                  });
                },
              })
            );
          })
        )
      ),
    })
  )
);
